import React from 'react';
import { Box, Text, Flex, HStack, Icon, Avatar, AvatarGroup } from '@chakra-ui/react';
import { FiBook, FiGrid, FiArrowUp, FiArrowDown, FiCheck } from 'react-icons/fi';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const TaskCard = React.memo(({ task, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
    id: task.id,
    data: {
      type: 'task',
      task,
    },
  });

  const getTypeIcon = type => {
    switch (type) {
      case 'story':
        return { icon: FiBook, bg: '#36B37E' };
      case 'task':
        return { icon: FiGrid, bg: '#0065FF' };
      case 'bug':
        return { bg: '#FF5630', isBug: true };
      default:
        return { icon: FiGrid, bg: '#6B778C' };
    }
  };

  const typeConfig = getTypeIcon(task.type);

  const handleClick = e => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (onClick && typeof onClick === 'function') {
      onClick(task);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        pointerEvents: isDragging ? 'none' : 'auto',
        userSelect: 'none',
      }}
      bg="white"
      p={4}
      mb={1}
      borderRadius="8px"
      border="1px solid"
      borderColor="#CBD5E0"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.12)"
      _hover={
        !isDragging
          ? {
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
              transform: 'translateY(-3px)',
            }
          : {}
      }
      transition="all 0.2s ease-in-out"
      cursor={isDragging ? 'grabbing' : 'pointer'}
    >
      <Text fontSize="11px" mb={2} color="#6B778C" fontWeight="600" textTransform="uppercase">
        {task.id}
      </Text>
      <Text fontSize="13px" mb={3} lineHeight="1.3" color="#172B4D">
        {task.title}
      </Text>
      <Flex justify="space-between" align="center">
        <HStack spacing={1}>
          {typeConfig.isBug ? (
            <Box
              w="16px"
              h="16px"
              bg={typeConfig.bg}
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="white" fontSize="8px" fontWeight="bold">
                !
              </Text>
            </Box>
          ) : (
            <Box
              w="16px"
              h="16px"
              bg={typeConfig.bg}
              borderRadius="2px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={typeConfig.icon} color="white" boxSize="10px" />
            </Box>
          )}
          {task.hasCheckMark && (
            <Box
              w="16px"
              h="16px"
              bg="#36B37E"
              borderRadius="2px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              ml={1}
            >
              <Icon as={FiCheck} color="white" boxSize="10px" />
            </Box>
          )}
        </HStack>
        <HStack spacing={1}>
          {task.hasUpArrow && <Icon as={FiArrowUp} color="#FF5630" boxSize="14px" />}
          {task.hasDownArrow && <Icon as={FiArrowDown} color="#2684FF" boxSize="14px" />}
          {task.assignees ? (
            <AvatarGroup size="sm" max={2} spacing="-8px">
              {task.assignees.map((assignee, index) => (
                <Avatar key={index} size="sm" src={assignee} w="24px" h="24px" border="2px solid white" />
              ))}
            </AvatarGroup>
          ) : task.assignee ? (
            <Avatar size="sm" name={task.assignee.name} w="24px" h="24px" />
          ) : null}
        </HStack>
      </Flex>
    </Box>
  );
});

export default TaskCard;